import getObjectKey from 'lib/object/getObjectKey'

/**
 * Redux/cache keys for item-policy API traffic.
 *
 * Most shapes use {@link getObjectKey}: object entries sorted **descending by property name**, then formatted as
 * `segment;segment` where each segment is `key=value`. Nested plain objects recurse into another key=value chain;
 * non-empty primitive arrays are sorted and comma-joined (`employeeIds=id-a,id-b`).
 */

/**
 * Budget search hotel GET shadow/cache key (`type: 'hotel_budget'` discriminant). `employeeIds` are sorted before serialisation.
 *
 * @example
 * ```
 * type=hotel_budget;roomCount=2;numberOfNights=3;lePlaceId=place_abc;employeeIds=emp-a,emp-b;currencyCode=AUD
 * ```
 */
export function generateItemPolicyHotelBudgetKey(params: {
  currencyCode: string
  longitude?: string
  latitude?: string
  lePlaceId?: string
  numberOfNights: number
  roomCount: number
  employeeIds: Array<string>
}): string {
  return getObjectKey({
    type: 'hotel_budget',
    ...params,
    employeeIds: [...params.employeeIds].sort(),
  })
}

/**
 * Budget search flight GET shadow/cache key (`type: 'flight_budget'` discriminant).
 *
 * @example
 * ```
 * type=flight_budget;flightViewType=return;employeeIds=emp-a,emp-b;departingAirportCode=MEL;currencyCode=AUD;arrivingAirportCode=SYD
 * ```
 */
export function generateItemPolicyFlightBudgetKey(params: {
  currencyCode: string
  departingAirportCode: string
  arrivingAirportCode: string
  flightViewType: string
  employeeIds: Array<string>
}): string {
  return getObjectKey({
    type: 'flight_budget',
    ...params,
    employeeIds: [...params.employeeIds].sort(),
  })
}

/**
 * Budget search car hire GET cache key (`type: 'car_hire_budget'`). No geography; `employeeIds` sorted.
 *
 * @example
 * ```
 * travellerCount=2;currencyCode=AUD;employeeIds=emp-a;numberOfDays=3;type=car_hire_budget
 * ```
 */
export function generateItemPolicyCarHireBudgetKey(params: {
  currencyCode: string
  numberOfDays: number
  travellerCount: number
  employeeIds: Array<string>
}): string {
  return getObjectKey({
    type: 'car_hire_budget',
    ...params,
    employeeIds: [...params.employeeIds].sort(),
  })
}

/**
 * Offer-page credits; prefixes offer id and suffixes sorted traveller ids (`__` separators).
 *
 * @example
 * ```
 * offer-111__travellerCount=1;roomCount=1;numberOfNights=2;location=longitude=144.96;latitude=-37.81;key=hotel_offer_xyz;itemPolicyType=HOTEL;currencyCode=AUD;cost=800__emp-a,emp-b
 * ```
 */
export function generateItemPolicyOfferCreditKey(
  item: App.BusinessTraveller.ItemPolicyEvaluationItem,
  offerId: string,
  employeeIds: Array<string>,
): string {
  return `${offerId}__${getObjectKey(item)}__${[...employeeIds].sort().join(',')}`
}

/**
 * Flight credits (`calculate` payload + travellers); optional trip-request prefix when browsing inside an approval.
 *
 * @example Without approval context:
 * ```
 * travellerCount=1;key=flight_MEL_SYD_450_return;itemPolicyType=FLIGHT;flightViewType=return;departingAirportCode=MEL;currencyCode=AUD;cost=450;arrivingAirportCode=SYD__emp-a,emp-b
 * ```
 *
 * @example With `tripRequestId`:
 * ```
 * req-999__travellerCount=1;key=flight_MEL_SYD_450_return;itemPolicyType=FLIGHT;flightViewType=return;departingAirportCode=MEL;currencyCode=AUD;cost=450;arrivingAirportCode=SYD__emp-a,emp-b
 * ```
 */
export function withTripRequestIdCachePrefix(cacheKey: string, tripRequestId?: string): string {
  if (!tripRequestId) {
    return cacheKey
  }
  return `${tripRequestId}__${cacheKey}`
}

export function generateItemPolicyFlightCreditKey(
  item: App.BusinessTraveller.ItemPolicyEvaluationItem,
  employeeIds: Array<string>,
  tripRequestId?: string,
): string {
  const base = `${getObjectKey(item)}__${[...employeeIds].sort().join(',')}`
  return withTripRequestIdCachePrefix(base, tripRequestId)
}
